import { createSlice, Slice } from '@reduxjs/toolkit';
import { SeverityType } from 'components/Toast';
import { splitStakeTotal } from 'utils/stakeSplit';

export type BetItemType = {
  id: string;
  timestamp: string;
  gameId: number | string;
  gameNumber: number;
  gameType: string;
  resultTimestamp: Date;
  market: 'Win' | 'Heads and Tails' | string;
  odd: number;
  picks: number[] | string[];
  amount: number;
  dirty: boolean;
  error: boolean;
  closeFn: Function | undefined;
  /** Sports: human-readable fixture title for print / slip */
  fixtureLabel?: string;
  /** Passed to Yami /api/bets/place so bet-service can resolve via scraper (game_id alone is not API-Football id). */
  homeTeam?: string;
  awayTeam?: string;
  startTime?: string;
  league?: string;
  sport?: string;
  matchLink?: string;
  rawOdds?: string;
  betType?: string;
  /** Sports: home | draw | away — sent to Yami bet service */
  selection?: string;
  /** Kickoff passed — highlight this line on the slip */
  kickoffBlocked?: boolean;
};

/** Optional: clear slip before add (e.g. replace entire slip with one pick). */
export type AddBetPayload = BetItemType & { replaceSlip?: boolean };

type BetSlipType = {
  betItems: BetItemType[];
  expiredItems: BetItemType[];
  // active tracks the current selected bet item id
  active: string;
  toast: null | { severity: SeverityType; message: string };
  globalStake: number;
  /** After cashier places a bet: same picks + stake can be restored (retail terminal). */
  cashierRebetSnapshot: { betItems: BetItemType[]; globalStake: number } | null;
  /** Web booking code loaded into slip — reuse on print after cashier payment. */
  sourceBookingCode: string | null;
};

const initialState: BetSlipType = {
  betItems: [],
  expiredItems: [],
  active: '',
  toast: null,
  globalStake: 0,
  cashierRebetSnapshot: null,
  sourceBookingCode: null,
};

const betSlipSlice: Slice<BetSlipType> = createSlice({
  name: 'betSlip',
  initialState,
  reducers: {
    addBet: (state, { payload }: { payload: AddBetPayload }) => {
      const { replaceSlip, ...raw } = payload;
      const picks = Array.isArray(raw.picks)
        ? raw.picks
        : [String((raw as BetItemType & { picks?: unknown }).picks ?? '')];
      if (replaceSlip) {
        state.betItems = [];
        state.globalStake = 0;
      }
      state.betItems.push({ ...raw, picks, dirty: false, error: false, kickoffBlocked: false });
      state.toast = null;
    },
    removeBet: (state, { payload }: { payload: BetItemType }) => {
      const index = state.betItems.findIndex((betItem) => betItem.id === payload.id);
      if (index !== -1) {
        state.betItems.splice(index, 1);
      }
      state.toast = null;
    },
    updateBetAmount: (state, { payload }) => {
      const index = state.betItems.findIndex((betItem) => betItem.id === payload.id);
      if (index !== -1) {
        state.betItems[index].amount = payload.amount;
      }
    },

    /** Full clear: lines, stake, and cashier rebet snapshot (explicit Clear / leave screen). */
    clearBet: (state) => {
      state.betItems = [];
      state.globalStake = 0;
      state.cashierRebetSnapshot = null;
      state.sourceBookingCode = null;
      state.toast = null;
    },
    /** After a successful place: empty the slip but keep `cashierRebetSnapshot` for Rebet. */
    clearBetSlipAfterPlace: (state) => {
      state.betItems = [];
      state.globalStake = 0;
      state.toast = null;
    },
    setActive: (state, { payload }) => {
      state.active = payload.id;
    },
    setDirty: (state, { payload }) => {
      const index = state.betItems.findIndex((betItem) => betItem.id === payload.id);
      if (index !== -1) {
        state.betItems[index].dirty = payload.status;
      }
    },
    setError: (state, { payload }) => {
      const index = state.betItems.findIndex((betItem) => betItem.id === payload.id);
      if (index !== -1) {
        state.betItems[index].error = payload.error;
      }
    },
    setKickoffBlocked: (state, { payload }: { payload: { ids: string[]; blocked: boolean } }) => {
      const idSet = new Set(payload.ids);
      state.betItems.forEach((item) => {
        if (idSet.has(item.id)) {
          item.kickoffBlocked = payload.blocked;
        }
      });
    },
    clearKickoffBlocked: (state) => {
      state.betItems.forEach((item) => {
        item.kickoffBlocked = false;
      });
    },
    setSourceBookingCode: (state, { payload }: { payload: string | null }) => {
      state.sourceBookingCode =
        typeof payload === 'string' && payload.trim() ? payload.trim() : null;
    },
    moveToExpired: (state, { payload }) => {
      state.expiredItems = state.betItems.filter((item) => item.gameId === payload.id);
      state.betItems = state.betItems.filter((item) => item.gameId !== payload.id);
    },
    clearExpired: (state) => {
      state.expiredItems = [];
    },
    setToast: (state, { payload }) => {
      if (payload == null) {
        state.toast = null;
        return;
      }
      const allowed = new Set(['success', 'info', 'warning', 'error']);
      const raw = payload as { severity?: unknown; message?: unknown } | null | undefined;
      const sev =
        typeof raw?.severity === 'string' && allowed.has(raw.severity) ? raw.severity : 'error';
      const msg =
        typeof raw?.message === 'string' ? raw.message : String(raw?.message ?? '');
      state.toast = { severity: sev as SeverityType, message: msg };
    },
    /** Total stake for multiples: split evenly across legs (not full amount on each leg).
     *  For a single leg, set the one item's amount directly. */
    setGlobalStake: (state, { payload }) => {
      const total = typeof payload === 'number' && Number.isFinite(payload) ? Number(payload.toFixed(2)) : 0;
      state.globalStake = total;
      const n = state.betItems.length;
      if (n >= 1) {
        const shares = splitStakeTotal(total, n);
        state.betItems.forEach((betItem, i) => {
          betItem.amount = shares[i] ?? 0;
        });
      }
    },
    /** Update combined stake display only; does not change per-line amounts. */
    setGlobalStakeDisplay: (state, { payload }) => {
      const total = typeof payload === 'number' && Number.isFinite(payload) ? payload : 0;
      state.globalStake = Math.max(0, Number(total.toFixed(2)));
    },
    setCashierRebetSnapshot: (
      state,
      { payload }: { payload: { betItems: BetItemType[]; globalStake: number } },
    ) => {
      state.cashierRebetSnapshot = {
        betItems: JSON.parse(JSON.stringify(payload.betItems)) as BetItemType[],
        globalStake: payload.globalStake,
      };
    },
    clearCashierRebetSnapshot: (state) => {
      state.cashierRebetSnapshot = null;
    },
    applyCashierRebet: (state) => {
      if (!state.cashierRebetSnapshot) return;
      const snap = state.cashierRebetSnapshot;
      const ts = new Date().toISOString();
      state.betItems = snap.betItems.map((b, i) => {
        const rt =
          b.resultTimestamp instanceof Date
            ? b.resultTimestamp
            : new Date(
              typeof b.resultTimestamp === 'string' ? b.resultTimestamp : Date.now(),
            );
        return {
          ...b,
          id: `rebet-${Date.now()}-${i}`,
          timestamp: ts,
          resultTimestamp: rt,
          closeFn: undefined,
          dirty: false,
          error: false,
          kickoffBlocked: false,
        };
      });
      state.globalStake = snap.globalStake;
      state.cashierRebetSnapshot = null;
      state.toast = null;
      const n = state.betItems.length;
      if (n >= 1 && state.globalStake > 0) {
        const shares = splitStakeTotal(state.globalStake, n);
        state.betItems.forEach((betItem, i) => {
          betItem.amount = shares[i] ?? 0;
        });
      }
    },
  },
});

// eslint-disable-next-line operator-linebreak
export const {
  addBet,
  removeBet,
  updateBetAmount,
  clearBet,
  clearBetSlipAfterPlace,
  setActive,
  setDirty,
  moveToExpired,
  clearExpired,
  setToast,
  setError,
  setKickoffBlocked,
  clearKickoffBlocked,
  setSourceBookingCode,
  setGlobalStake,
  setGlobalStakeDisplay,
  setCashierRebetSnapshot,
  clearCashierRebetSnapshot,
  applyCashierRebet,
} = betSlipSlice.actions;
export default betSlipSlice.reducer;
